import { io } from 'socket.io-client';

import { config } from '@/config';
import { sentryCaptureSocketError } from '@/utils/sentry/sentry-socket-error';

import { NotificationsServerToClientEvents, NotificationsSocket } from './types';

class NotificationsSocketService {
  private socket: NotificationsSocket | null = null;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  connect() {
    if (this.socket?.connected) {
      return this.socket;
    }

    this.socket = io(config.baseUrl.replace('https', 'wss'), {
      withCredentials: true,
      transports: ['websocket'],
      path: '/notifications/socket.io',
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
    });

    this.setupEventListeners();
    return this.socket;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  getSocket(): NotificationsSocket | null {
    return this.socket;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Event listener management
  on<K extends keyof NotificationsServerToClientEvents>(
    event: K,
    listener: NotificationsServerToClientEvents[K],
  ): void {
    /* @ts-expect-error: The Socket.IO library uses heavily overloaded types  */
    this.socket?.on(event, listener);
  }

  off<K extends keyof NotificationsServerToClientEvents>(
    event: K,
    listener?: NotificationsServerToClientEvents[K],
  ): void {
    /* @ts-expect-error: The Socket.IO library uses heavily overloaded types  */
    this.socket?.off(event, listener);
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Notifications socket connected');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Notifications socket disconnected:', reason);
      sentryCaptureSocketError({
        socketType: 'notifications',
        event: 'disconnect',
        reason,
      });
    });

    this.socket.on('connect_error', (error) => {
      console.error('Notifications socket connection error:', error);
      sentryCaptureSocketError({
        socketType: 'notifications',
        event: 'connect_error',
        error,
      });
    });
  }
}

export const notificationsSocketService = new NotificationsSocketService();
